// frontend/src/components/resume/ResumeCard.tsx

import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { Card } from '@/components/common/UI/Card'
import { Badge } from '@/components/common/UI/Badge'
import { Button } from '@/components/common/UI/Button'
import { Modal, useModal } from '@/components/common/UI/Modal'
import { useAuthStore } from '@/store'
import {
  formatDate,
  formatCurrency,
  getStatusColor,
  truncateText,
  getInitialsAvatar,
  generateRandomColor
} from '@/utils/helpers'

// ============================================
// Компонент оценки резюме (AI Score)
// ============================================

interface ResumeScoreProps {
  resumeId: number
  compact?: boolean
}

export const ResumeScore: React.FC<ResumeScoreProps> = ({
  resumeId,
  compact = false
}) => {
  const { data: score, isLoading } = useQuery({
    queryKey: ['resume', resumeId, 'score'],
    queryFn: async () => {
      return apiClient.get(`/v1/resumes/${resumeId}/score`)
    },
    enabled: !!resumeId
  })

  if (isLoading || !score) {
    return null
  }

  const getScoreColor = (value: number) => {
    if (value >= 80) return 'bg-success'
    if (value >= 60) return 'bg-warning'
    return 'bg-error'
  }

  const getScoreLabel = (value: number) => {
    if (value >= 80) return 'Отлично'
    if (value >= 60) return 'Хорошо'
    if (value >= 40) return 'Средне'
    return 'Требует доработки'
  }

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white bg-primary-600">
          {score.score || 0}
        </div>
        <div>
          <p className="text-xs font-medium">{getScoreLabel(score.score)}</p>
          <p className="text-xs text-secondary-400">
            Совпадение: {score.match_percentage || 0}%
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Общая оценка</span>
        <span className="text-lg font-bold text-primary-600">{score.score || 0}%</span>
      </div>
      <div className="w-full h-2 bg-secondary-200 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-500 ${getScoreColor(score.score)}`}
          style={{ width: `${score.score || 0}%` }}
        />
      </div>
      <p className="text-xs text-secondary-500">{score.summary}</p>

      {score.strengths && score.strengths.length > 0 && (
        <div>
          <p className="text-xs font-medium text-success">Сильные стороны:</p>
          <ul className="text-xs text-secondary-600 list-disc list-inside">
            {score.strengths.slice(0, 3).map((item: string, idx: number) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </div>
      )}

      {score.weaknesses && score.weaknesses.length > 0 && (
        <div>
          <p className="text-xs font-medium text-error">Что улучшить:</p>
          <ul className="text-xs text-secondary-600 list-disc list-inside">
            {score.weaknesses.slice(0, 3).map((item: string, idx: number) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

// ============================================
// Компонент карточки резюме
// ============================================

interface ResumeCardProps {
  resume: any
  compact?: boolean
  showScore?: boolean
  onApply?: () => void
  onEdit?: () => void
  onDelete?: () => void
  actions?: React.ReactNode
}

export const ResumeCard: React.FC<ResumeCardProps> = ({
  resume,
  compact = false,
  showScore = true,
  onApply,
  onEdit,
  onDelete,
  actions
}) => {
  const { user, isAuthenticated } = useAuthStore()
  const [showFullDescription, setShowFullDescription] = useState(false)
  const deleteModal = useModal()
  const isOwner = user?.id === resume?.user_id
  const isEmployer = user?.role === 'employer' || user?.role === 'admin'

  const avatarColor = generateRandomColor()
  const initials = getInitialsAvatar(
    resume?.user?.full_name || resume?.user?.first_name || 'Пользователь'
  )

  // Удаление резюме
  const handleDelete = async () => {
    try {
      await apiClient.delete(`/v1/resumes/${resume.id}`)
      if (onDelete) onDelete()
      deleteModal.close()
    } catch (error) {
      console.error('Error deleting resume:', error)
    }
  }

  // Компактная версия
  if (compact) {
    return (
      <Card className="p-4 hover:shadow-md transition-shadow">
        <Link to={`/resumes/${resume.id}`}>
          <div className="flex items-start gap-3">
            {/* Аватар */}
            {resume?.user?.avatar_url ? (
              <img
                src={resume.user.avatar_url}
                alt={resume.user.full_name}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                style={{ backgroundColor: avatarColor }}
              >
                {initials}
              </div>
            )}

            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-secondary-900 hover:text-primary-600 transition-colors truncate">
                {resume.title || resume.position}
              </h4>
              {resume.is_highlighted && <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800"><span aria-hidden="true">★</span> Резюме в топе</span>}
              <p className="text-sm text-secondary-600 truncate">
                {resume.user?.full_name || resume.user?.first_name || 'Пользователь'}
              </p>
              <div className="flex items-center gap-3 mt-1 text-xs text-secondary-400">
                {resume.city && <span>{resume.city}</span>}
                {resume.salary_from && (
                  <span>
                    {formatCurrency(resume.salary_from, resume.salary_currency)}
                    {resume.salary_to && ` - ${formatCurrency(resume.salary_to, resume.salary_currency)}`}
                  </span>
                )}
                {resume.experience_level && (
                  <span>{resume.experience_level.replace('_', ' ')}</span>
                )}
              </div>
            </div>

            {showScore && (
              <ResumeScore resumeId={resume.id} compact />
            )}
          </div>
        </Link>
      </Card>
    )
  }

  // Полная версия
  return (
    <Card className="p-6 hover:shadow-md transition-shadow">
      <div className="flex flex-col md:flex-row md:items-start gap-4">
        {/* Левая часть - аватар */}
        <div className="flex-shrink-0">
          {resume?.user?.avatar_url ? (
            <img
              src={resume.user.avatar_url}
              alt={resume.user.full_name}
              className="w-16 h-16 rounded-full object-cover"
            />
          ) : (
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold text-white flex-shrink-0"
              style={{ backgroundColor: avatarColor }}
            >
              {initials}
            </div>
          )}
        </div>

        {/* Центральная часть - информация */}
        <div className="flex-1 min-w-0">
          {/* Заголовок */}
          <Link to={`/resumes/${resume.id}`}>
            <div className="flex flex-wrap items-center gap-2"><h3 className="text-lg font-semibold text-secondary-900 hover:text-primary-600 transition-colors">{resume.title || resume.position}</h3>{resume.is_highlighted && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800"><span aria-hidden="true">★</span> Резюме в топе</span>}</div>
          </Link>

          {/* Пользователь */}
          <p className="text-secondary-600 text-sm">
            {resume.user?.full_name || resume.user?.first_name || 'Пользователь'}
          </p>

          {/* Детали */}
          <div className="flex flex-wrap gap-3 mt-2 text-sm text-secondary-600">
            {resume.city && (
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {resume.city}
              </span>
            )}
            {resume.salary_from && (
              <span className="font-medium text-secondary-900">
                {formatCurrency(resume.salary_from, resume.salary_currency)}
                {resume.salary_to && ` - ${formatCurrency(resume.salary_to, resume.salary_currency)}`}
              </span>
            )}
            {resume.experience_level && (
              <span>
                Опыт: {resume.experience_level.replace('_', ' ')}
              </span>
            )}
            {resume.remote_ready && (
              <span className="text-green-600">🌐 Готов к удаленной работе</span>
            )}
            {resume.relocation_ready && (
              <span className="text-blue-600">📦 Готов к переезду</span>
            )}
          </div>

          {/* Навыки */}
          {resume.skills && resume.skills.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-3">
              {resume.skills.slice(0, 8).map((skill: string) => (
                <span
                  key={skill}
                  className="px-2 py-0.5 bg-primary-50 text-primary-700 rounded text-xs"
                >
                  {skill}
                </span>
              ))}
              {resume.skills.length > 8 && (
                <span className="text-xs text-secondary-400">
                  +{resume.skills.length - 8}
                </span>
              )}
            </div>
          )}

          {/* Опыт работы (кратко) */}
          {resume.experience && resume.experience.length > 0 && (
            <div className="mt-3 space-y-1">
              {resume.experience.slice(0, 2).map((exp: any, idx: number) => (
                <p key={idx} className="text-sm text-secondary-600">
                  <span className="font-medium">{exp.position}</span>
                  {' — '}
                  <span className="text-secondary-500">{exp.company}</span>
                  {exp.is_current && (
                    <span className="ml-2 text-xs text-success">(текущая)</span>
                  )}
                </p>
              ))}
              {resume.experience.length > 2 && (
                <p className="text-xs text-secondary-400">
                  + еще {resume.experience.length - 2} мест работы
                </p>
              )}
            </div>
          )}

          {/* Описание */}
          {resume.summary && (
            <p className="mt-3 text-sm text-secondary-600">
              {showFullDescription ? resume.summary : truncateText(resume.summary, 200)}
              {resume.summary.length > 200 && (
                <button
                  onClick={() => setShowFullDescription(!showFullDescription)}
                  className="ml-1 text-primary-600 hover:text-primary-700 text-sm"
                >
                  {showFullDescription ? 'Скрыть' : 'Читать далее'}
                </button>
              )}
            </p>
          )}

          {/* Статус и дата */}
          <div className="flex items-center gap-3 mt-3 text-xs text-secondary-400">
            <Badge className={getStatusColor(resume.is_public ? 'approved' : 'draft')}>
              {resume.is_public ? 'Опубликовано' : 'Черновик'}
            </Badge>
            <span>Обновлено: {formatDate(resume.updated_at)}</span>
            {resume.views_count > 0 && (
              <span>👁️ {resume.views_count} просмотров</span>
            )}
          </div>
        </div>

        {/* Правая часть - действия */}
        <div className="flex flex-col items-end gap-2 min-w-[140px]">
          {showScore && (
            <ResumeScore resumeId={resume.id} compact />
          )}

          {actions ? (
            actions
          ) : (
            <>
              {isOwner && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onEdit || (() => window.location.href = `/resumes/${resume.id}/edit`)}
                  >
                    Редактировать
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={deleteModal.open}
                  >
                    Удалить
                  </Button>
                </>
              )}

              {isEmployer && !isOwner && (
                <Button
                  size="sm"
                  onClick={onApply || (() => window.location.href = `/resumes/${resume.id}`)}
                >
                  Просмотреть
                </Button>
              )}

              {!isAuthenticated && (
                <Button
                  size="sm"
                  onClick={() => window.location.href = '/login'}
                >
                  Войти для просмотра
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Модалка подтверждения удаления */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        title="Удаление резюме"
      >
        <div className="py-4">
          <p className="text-secondary-600">
            Вы уверены, что хотите удалить резюме{' '}
            <span className="font-medium text-secondary-900">
              {resume.title || resume.position}
            </span>
            ?
          </p>
          <p className="text-sm text-error mt-2">
            Это действие нельзя отменить.
          </p>
        </div>
        <div className="flex justify-end gap-3 border-t border-secondary-200 pt-4">
          <Button variant="ghost" onClick={deleteModal.close}>
            Отмена
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Удалить
          </Button>
        </div>
      </Modal>
    </Card>
  )
}

// ============================================
// Компонент списка резюме
// ============================================

interface ResumeListProps {
  resumes: any[]
  loading?: boolean
  showScore?: boolean
  onApply?: (resume: any) => void
  onEdit?: (resume: any) => void
  onDelete?: (resume: any) => void
  emptyMessage?: string
}

export const ResumeList: React.FC<ResumeListProps> = ({
  resumes,
  loading = false,
  showScore = true,
  onApply,
  onEdit,
  onDelete,
  emptyMessage = 'Резюме не найдены'
}) => {
  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="p-6 animate-pulse">
            <div className="flex gap-4">
              <div className="w-16 h-16 rounded-full bg-secondary-200" />
              <div className="flex-1 space-y-3">
                <div className="h-5 bg-secondary-200 rounded w-1/3" />
                <div className="h-4 bg-secondary-200 rounded w-1/4" />
                <div className="h-4 bg-secondary-200 rounded w-2/3" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    )
  }

  if (resumes.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="w-16 h-16 mx-auto text-secondary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p className="mt-4 text-secondary-500">{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {resumes.map((resume) => (
        <ResumeCard
          key={resume.id}
          resume={resume}
          showScore={showScore}
          {...(onApply ? { onApply: () => onApply(resume) } : {})}
          {...(onEdit ? { onEdit: () => onEdit(resume) } : {})}
          {...(onDelete ? { onDelete: () => onDelete(resume) } : {})}
        />
      ))}
    </div>
  )
}

// ============================================
// Экспорт
// ============================================

export default ResumeCard
